import { useCallback, useEffect, useRef, useState } from 'react';

import {
    createEntityAttribute,
    getEntityAttributeFilterParams,
    getEntityMasterFilterOptions,
    getEntityMasterTable,
    getMatchingEntityIds,
    type EntityMasterTableResponse,
    type EntityType,
    type FilterParamResponse,
    type MasterEntityType,
    type MasterTableColumnMeta,
} from '@api/entity-attributes';
import type { DataSheetFilterConfig, FilterValue } from '@components/ui/DataSheet';

import { attributeEntityType } from './entityMaster.constants';

export const DEFAULT_PAGE_SIZE = 50;

interface UseEntityMasterReturn {
    rows: Record<string, unknown>[];
    columns: MasterTableColumnMeta[];
    total: number;
    page: number;
    pageSize: number;
    isLoading: boolean;
    search: string;
    setSearch: (v: string) => void;
    setPage: (p: number) => void;
    setPageSize: (s: number) => void;
    reload: () => void;
    filterConfigs: DataSheetFilterConfig[];
    addColumn: (name: string, showInFilter: boolean) => Promise<string>;
    /** Every row the current view would show, unpaginated — for the CSV export. */
    fetchAll: () => Promise<Record<string, unknown>[]>;
}

/**
 * One page big enough to be the whole table.
 *
 * There is no dedicated export endpoint and none is needed: `pageSize` carries no
 * server-side maximum, and the same pattern already backs the inventory exports. The
 * largest master table in production is under 3k rows, so this is one query, not a
 * paging loop that could tear if somebody edits a row midway through it.
 */
const EXPORT_PAGE_SIZE = 1_000_000;

/**
 * What a tab filters on before anyone touches the filter. The two mapping grids open
 * on active products / materials only: a retired product keeps its vendor rows, and
 * for a brand like Troovy those outnumber the live ones. Clearing the filter shows all.
 * The values are the strings the options endpoint returns for the boolean, as the
 * Products tab prints them.
 */
const DEFAULT_FILTER_VALUE: Partial<Record<MasterEntityType, FilterValue>> = {
    VENDOR_PRODUCT_MAPPING: { isActive: ['true'] },
    VENDOR_MATERIAL_MAPPING: { isActive: ['true'] },
};

const defaultFilterValue = (entityType: MasterEntityType): FilterValue => ({
    ...(DEFAULT_FILTER_VALUE[entityType] ?? {}),
});

/** Split a combined filterValue into coreFilters and attribute filterEntityIds */
async function resolveFilters(
    filterValue: FilterValue,
    attrParams: FilterParamResponse[],
): Promise<{ coreFilters: Record<string, string[]>; filterEntityIds?: string[] }> {
    const coreFilters: Record<string, string[]> = {};
    const attrEntries: [string, string[]][] = [];

    for (const [key, values] of Object.entries(filterValue)) {
        if (!values || values.length === 0) continue;
        if (key.startsWith('attr:')) {
            attrEntries.push([key, values]);
        } else {
            coreFilters[key] = values;
        }
    }

    if (attrEntries.length === 0) return { coreFilters };

    const idSets = await Promise.all(
        attrEntries.map(async ([key, values]) => {
            const attributeId = key.slice(5); // strip 'attr:'
            const result = await getMatchingEntityIds(attributeId, values);
            return new Set(result.entityIds);
        }),
    );

    const [first, ...rest] = idSets;
    const intersected = rest.reduce(
        (acc, set) => new Set([...acc].filter((id) => set.has(id))),
        first,
    );
    return { coreFilters, filterEntityIds: [...intersected] };
}

/**
 * What a page may hand the grid about its search and filters.
 *
 * The Catalog page keeps both in the URL so that Back from a product returns to the same
 * list. The hook stays the owner of the state; the page only seeds it and hears changes.
 */
export interface EntityMasterStateOptions {
    initialSearch?: string;
    initialFilterValue?: FilterValue;
    onSearchChange?: (search: string) => void;
    onFilterValueChange?: (filterValue: FilterValue) => void;
}

export function useEntityMaster(
    entityType: MasterEntityType,
    options: EntityMasterStateOptions = {},
): UseEntityMasterReturn {
    const [data, setData] = useState<EntityMasterTableResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearchRaw] = useState(options.initialSearch ?? '');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSizeRaw] = useState(DEFAULT_PAGE_SIZE);

    // Core filter options (distinct values from entity columns)
    const [coreFilterOptions, setCoreFilterOptions] = useState<Record<string, string[]>>({});
    // Attribute filter params (showInFilter custom attributes)
    const [attrParams, setAttrParams] = useState<FilterParamResponse[]>([]);
    // Combined filter state
    const [filterValue, setFilterValueRaw] = useState<FilterValue>(
        () => options.initialFilterValue ?? defaultFilterValue(entityType),
    );
    // The page's listeners, read through a ref so they never retrigger the loaders.
    const listeners = useRef(options);
    listeners.current = options;

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Keep latest values accessible without re-creating callbacks
    const stateRef = useRef({ search, page, pageSize, filterValue, attrParams });
    stateRef.current = { search, page, pageSize, filterValue, attrParams };

    const load = useCallback(async (
        p: number,
        s: string,
        ps: number,
        fv: FilterValue,
        ap: FilterParamResponse[],
    ) => {
        setIsLoading(true);
        try {
            const { coreFilters, filterEntityIds } = await resolveFilters(fv, ap);
            const result = await getEntityMasterTable({
                entityType,
                page: p,
                pageSize: ps,
                search: s || undefined,
                filterEntityIds,
                coreFilters: Object.keys(coreFilters).length > 0 ? coreFilters : undefined,
            });
            setData(result);
        } catch {
            // keep stale data
        } finally {
            setIsLoading(false);
        }
    }, [entityType]);

    // Load filter options when entity type changes
    useEffect(() => {
        setCoreFilterOptions({});
        setAttrParams([]);
        setFilterValueRaw(listeners.current.initialFilterValue ?? defaultFilterValue(entityType));
        setPage(1);

        Promise.all([
            getEntityMasterFilterOptions(entityType).catch(() => ({} as Record<string, string[]>)),
            attributeEntityType(entityType)
                ? getEntityAttributeFilterParams(
                      attributeEntityType(entityType) as EntityType,
                  ).catch(() => [] as FilterParamResponse[])
                : Promise.resolve([] as FilterParamResponse[]),
        ]).then(([coreOpts, attrPs]) => {
            setCoreFilterOptions(coreOpts);
            setAttrParams(attrPs);
        });
    }, [entityType]);

    // Load data when page or pageSize changes
    useEffect(() => {
        const { search: s, filterValue: fv, attrParams: ap } = stateRef.current;
        void load(page, s, pageSize, fv, ap);
    }, [page, pageSize, load]);

    const setSearch = useCallback((v: string) => {
        setSearchRaw(v);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            const { pageSize: ps, filterValue: fv, attrParams: ap } = stateRef.current;
            void load(1, v, ps, fv, ap);
            listeners.current.onSearchChange?.(v);
        }, 350);
    }, [load]);

    const setPageSize = useCallback((s: number) => {
        setPageSizeRaw(s);
        setPage(1);
        const { search: sr, filterValue: fv, attrParams: ap } = stateRef.current;
        void load(1, sr, s, fv, ap);
    }, [load]);

    const setFilterValue = useCallback((fv: FilterValue) => {
        setFilterValueRaw(fv);
        setPage(1);
        const { search: s, pageSize: ps, attrParams: ap } = stateRef.current;
        void load(1, s, ps, fv, ap);
        listeners.current.onFilterValueChange?.(fv);
    }, [load]);

    const reload = useCallback(() => {
        const { search: s, page: p, pageSize: ps, filterValue: fv, attrParams: ap } = stateRef.current;
        void load(p, s, ps, fv, ap);
    }, [load]);

    // Build DataSheetFilterConfig[] from core options + attribute params
    const filterConfigs = useCallback((): DataSheetFilterConfig[] => {
        const config = ENTITY_FILTER_LABEL[entityType];
        const params = [
            ...Object.entries(coreFilterOptions)
                .filter(([, vals]) => vals.length > 0)
                .map(([key, vals]) => ({
                    key,
                    label: CORE_COL_LABEL[entityType]?.[key] ?? key,
                    mode: 'multi' as const,
                    options: vals.map((v) => ({ label: v, value: v })),
                })),
            ...attrParams.map((p) => ({
                key: `attr:${p.key}`,
                label: p.label,
                mode: p.mode,
                options: p.options,
            })),
        ];

        if (params.length === 0) return [];
        return [{ label: config, params, value: filterValue, onChange: setFilterValue }];
    }, [coreFilterOptions, attrParams, filterValue, setFilterValue, entityType]);

    /**
     * The rows an export writes.
     *
     * Deliberately re-runs the SAME query the grid is showing, search and filters
     * included. Somebody who has narrowed to one category is asking for that category,
     * and a download that quietly widened to the whole catalogue would come back edited
     * as if it were the filtered set — every row they never looked at riding along.
     *
     * Errors are not swallowed the way `load` swallows them: `load` can keep showing
     * stale rows, but a download that fails has nothing to fall back on, and a button
     * that silently does nothing is worse than one that reports why.
     */
    const fetchAll = useCallback(async (): Promise<Record<string, unknown>[]> => {
        const { search: s, filterValue: fv, attrParams: ap } = stateRef.current;
        const { coreFilters, filterEntityIds } = await resolveFilters(fv, ap);

        const result = await getEntityMasterTable({
            entityType,
            page: 1,
            pageSize: EXPORT_PAGE_SIZE,
            search: s || undefined,
            filterEntityIds,
            coreFilters:
                Object.keys(coreFilters).length > 0 ? coreFilters : undefined,
        });

        return result.rows;
    }, [entityType]);

    const addColumn = useCallback(async (name: string, showInFilter: boolean): Promise<string> => {
        const attrEntity = attributeEntityType(entityType);
        if (!attrEntity) {
            throw new Error(
                'Custom columns are not available on this table — it is keyed by a pair, and an attribute can only be stored against one id.',
            );
        }
        const attr = await createEntityAttribute({ entityType: attrEntity, name, showInFilter });
        const { search: s, page: p, pageSize: ps, filterValue: fv, attrParams: ap } = stateRef.current;
        void load(p, s, ps, fv, ap);
        return attr.id;
    }, [entityType, load]);

    return {
        rows: data?.rows ?? [],
        columns: data?.columns ?? [],
        total: data?.total ?? 0,
        page,
        pageSize,
        isLoading,
        search,
        setSearch,
        setPage,
        setPageSize,
        reload,
        filterConfigs: filterConfigs(),
        addColumn,
        fetchAll,
    };
}

const ENTITY_FILTER_LABEL: Record<MasterEntityType, string> = {
    PRODUCT: 'Product',
    VENDOR: 'Vendor',
    VENDOR_PRODUCT_MAPPING: 'Mapping',
    VENDOR_MATERIAL_MAPPING: 'Mapping',
    VENDOR_PRICE: 'Price line',
    WAREHOUSE: 'Warehouse',
    CHANNEL: 'Channel',
    MATERIAL: 'Material',
};

const CORE_COL_LABEL: Record<MasterEntityType, Record<string, string>> = {
    PRODUCT:   { category: 'Category', productGroup: 'Product Group', productType: 'Type', vendors: 'Vendor', isActive: 'Active' },
    VENDOR:    { city: 'City', country: 'Country', type: 'Type', incoterms: 'Incoterms' },
    VENDOR_PRODUCT_MAPPING: { vendorId: 'Vendor', currency: 'Currency', category: 'Category', isActive: 'Active' },
    VENDOR_MATERIAL_MAPPING: { vendorId: 'Vendor', currency: 'Currency', category: 'Category', isActive: 'Active' },
    VENDOR_PRICE: { vendorId: 'Vendor', itemType: 'Type', currency: 'Currency', terms: 'Terms', source: 'Source' },
    WAREHOUSE: { city: 'City', state: 'State', type: 'Type' },
    CHANNEL:   { type: 'Type', model: 'Model' },
    MATERIAL:  { category: 'Category', materialType: 'Type', unit: 'Unit', vendors: 'Vendor', isActive: 'Active' },
};

